import React from 'react';
import { Alert, Table, Tag, Typography } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

import {
  cadenceLabel,
  kindLabel,
  moveUnitOf,
  overlapPercent,
  PREGRADE_COLOURS,
  readable,
} from './format';
import { Instrument, Overlap, Preview, PreviewCheck } from './types';

/* ------------------------------------------------------------------ *
 * Instruments
 * ------------------------------------------------------------------ */

const FamilyDetail: React.FC<{ family: NonNullable<Instrument['family']> }> = ({
  family,
}) => {
  if (!family.ok) {
    return (
      <Alert
        type="error"
        showIcon
        title={`Bond family ${family.code} does not resolve`}
        description={family.error || 'No reason was given.'}
      />
    );
  }
  return (
    <div className="blueprint-builder__family">
      <Typography.Text type="secondary">
        Family {family.code}: {family.issues.length} issue
        {family.issues.length === 1 ? '' : 's'}
      </Typography.Text>
      <ul>
        {family.issues.map(issue => (
          <li key={issue.asset_id}>
            {issue.name}{' '}
            <Typography.Text type="secondary">
              ({issue.first_landing || '?'} → {issue.last_landing || '?'})
            </Typography.Text>
          </li>
        ))}
      </ul>
      <Typography.Text type="secondary">
        Rolls on:{' '}
        {family.roll_landings.length
          ? family.roll_landings.join(', ')
          : 'no roll inside this window'}
      </Typography.Text>
    </div>
  );
};

/**
 * Each instrument with its kind and the unit it moves in (bp for a bond, %
 * for every other kind). Only a bond has a synthetic family to show; a kind
 * this screen does not know is shown by name, as a price instrument.
 */
export const InstrumentsSection: React.FC<{ instruments: Instrument[] }> = ({
  instruments,
}) =>
  instruments.length ? (
    <ul className="blueprint-builder__instruments">
      {instruments.map(instrument => (
        <li key={instrument.code} data-testid={`instrument-${instrument.code}`}>
          <div className="blueprint-builder__instrument-head">
            <Typography.Text strong>{instrument.code}</Typography.Text>
            {instrument.label && instrument.label !== instrument.code && (
              <Typography.Text type="secondary">
                {instrument.label}
              </Typography.Text>
            )}
            {instrument.kind && <Tag>{kindLabel(instrument.kind)}</Tag>}
            <Tag>{moveUnitOf(instrument)}</Tag>
            {instrument.graded_as ? (
              <Tag color="green">graded as {instrument.graded_as}</Tag>
            ) : (
              <Tag color="warning">ungraded</Tag>
            )}
          </div>
          {instrument.direction_note && (
            <Typography.Text type="secondary">
              {instrument.direction_note}
            </Typography.Text>
          )}
          {instrument.kind === 'bond' && instrument.family && (
            <FamilyDetail family={instrument.family} />
          )}
          {instrument.kind === 'bond' && !instrument.family && (
            <Alert
              type="warning"
              showIcon
              title="No bond family: this bond cannot be traded in a blueprint."
            />
          )}
        </li>
      ))}
    </ul>
  ) : (
    <Typography.Text type="secondary">No instruments.</Typography.Text>
  );

/* ------------------------------------------------------------------ *
 * Checks
 * ------------------------------------------------------------------ */

const CHECK_ICONS: Record<PreviewCheck['status'], React.ReactNode> = {
  ok: (
    <CheckCircleFilled className="blueprint-builder__icon-ok" aria-label="ok" />
  ),
  warn: (
    <ExclamationCircleFilled
      className="blueprint-builder__icon-warn"
      aria-label="warning"
    />
  ),
  fail: (
    <CloseCircleFilled
      className="blueprint-builder__icon-fail"
      aria-label="failed"
    />
  ),
  info: (
    <InfoCircleFilled
      className="blueprint-builder__icon-info"
      aria-label="information"
    />
  ),
};

export const ChecksSection: React.FC<{ checks: PreviewCheck[] }> = ({
  checks,
}) =>
  checks.length ? (
    <ul className="blueprint-builder__checks">
      {checks.map(check => (
        <li key={check.key} data-testid={`check-${check.key}`}>
          {CHECK_ICONS[check.status] || CHECK_ICONS.info}
          <div>
            <Typography.Text strong>{check.label}</Typography.Text>
            {check.detail && (
              <div>
                <Typography.Text type="secondary">
                  {check.detail}
                </Typography.Text>
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  ) : (
    <Typography.Text type="secondary">No checks were run.</Typography.Text>
  );

/* ------------------------------------------------------------------ *
 * Overlap
 * ------------------------------------------------------------------ */

const overlapColumns: ColumnsType<Overlap> = [
  {
    title: 'Blueprint',
    key: 'name',
    render: (_: unknown, row: Overlap) => (
      <Link to={`/dashboard/blueprints/${row.blueprint_id}`}>
        {row.name}{' '}
        <Typography.Text type="secondary">#{row.blueprint_id}</Typography.Text>
      </Link>
    ),
  },
  {
    title: 'Overlap',
    key: 'fraction',
    width: 90,
    render: (_: unknown, row: Overlap) => overlapPercent(row.fraction),
  },
  {
    title: 'Cadence',
    key: 'cadence',
    width: 130,
    render: (_: unknown, row: Overlap) => (
      <span>
        {cadenceLabel(row.cadence_days)}{' '}
        {!row.same_cadence && <Tag>different cadence</Tag>}
      </span>
    ),
  },
  {
    title: 'Trainers see it',
    key: 'visible',
    width: 110,
    render: (_: unknown, row: Overlap) => (row.visible ? 'yes' : 'no'),
  },
  {
    title: 'Status',
    key: 'status',
    width: 110,
    render: (_: unknown, row: Overlap) => readable(row.status || ''),
  },
];

export const OverlapSection: React.FC<{ overlap: Overlap[] }> = ({
  overlap,
}) =>
  overlap.length ? (
    <Table<Overlap>
      size="small"
      pagination={false}
      rowKey="blueprint_id"
      columns={overlapColumns}
      dataSource={overlap}
      scroll={{ x: 'max-content' }}
    />
  ) : (
    <Typography.Text type="secondary">
      No existing blueprint covers this window.
    </Typography.Text>
  );

/* ------------------------------------------------------------------ *
 * Pre-grade
 * ------------------------------------------------------------------ */

export const PregradeSection: React.FC<{ pregrade: Preview['pregrade'] }> = ({
  pregrade,
}) => (
  <div className="blueprint-builder__pregrade">
    <Tag color={PREGRADE_COLOURS[pregrade.status]}>
      {pregrade.status === 'not_predicted'
        ? 'not predicted'
        : pregrade.status.toUpperCase()}
    </Tag>
    {pregrade.detail && (
      <Typography.Text type="secondary">{pregrade.detail}</Typography.Text>
    )}
  </div>
);

/* ------------------------------------------------------------------ *
 * Trainee-facing text
 * ------------------------------------------------------------------ */

export const TraineeSection: React.FC<{ trainee: Preview['trainee'] }> = ({
  trainee,
}) => (
  <div className="blueprint-builder__trainee" data-testid="trainee-text">
    <Typography.Text type="secondary" className="blueprint-builder__label">
      What trainees will see
    </Typography.Text>
    <div className="blueprint-builder__trainee-title">
      {trainee.title || <em>No title yet</em>}
    </div>
    <div className="blueprint-builder__text">
      {trainee.brief || <em>No brief yet</em>}
    </div>
    {trainee.leaks.length > 0 && (
      <Alert
        type="warning"
        showIcon
        title="This text may leak hindsight to trainees"
        description={
          <ul className="blueprint-builder__leaks">
            {trainee.leaks.map(leak => (
              <li key={leak}>{leak}</li>
            ))}
          </ul>
        }
      />
    )}
  </div>
);
